import { cnl } from "csi/typings";
import { action, computed, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";

const OUTER_PADDING = 44;	// should be 48, but grid has negative margin on each side
const WIDTH_PER_COLOR = 28;

export interface ColorSpec {
	/** validity of the chip */
	hasViolation?: boolean;

	/** unique identifier for the chip */
	id?: string;

	/** src for the image displayed in the chip */
	image?: string;

	/** name of the chip */
	name: string;
}

export default class ColorChipArrayStore {
	@observable.ref private _colors: ColorSpec[];
	private readonly _defaultColor: ColorSpec;
	@observable private _chipsPerPage = 1;
	@observable private _page = 0;
	@observable.ref private _value: ColorSpec;
	@observable private _width = 0;

	/** return true if there are pages after selected one */
	get canNextPage(): boolean {
		return (this._page + 1) < this.numberOfPages;
	}

	/** return true if there are pages before selected one */
	get canPrevPage(): boolean {
		return this._page > 0;
	}

	/** exact width taken by chips on a single page */
	get chipsWidth(): number {
		return this._chipsPerPage * WIDTH_PER_COLOR;
	}

	/** id list of colors */
	get colorIds(): cnl[] {
		return this.colors.map((color) => color.id);
	}

	/** all colors to display */
	get colors(): ColorSpec[] {
		return this._colors;
	}

	/** get the colors to be displayed on the current page */
	@computed get displayedColorList(): ColorSpec[] {
		const { _chipsPerPage, _colors, _page, _width } = this;
		const index = _page * _chipsPerPage;
		return _width ? _colors.slice(index, index + _chipsPerPage) : [];
	}

	/** indicates if there are multiple pages of colors */
	get hasMultiplePages(): boolean {
		return this.numberOfPages > 1;
	}

	/** number of pages */
	get numberOfPages(): number {
		return Math.ceil((1.0 * this._colors.length) / this._chipsPerPage);
	}

	/** get currently selected chip */
	@computed get value(): ColorSpec {
		return this._value;
	}

	/** return true if given chip is the default one */
	isDefaultColor = (chip: ColorSpec): boolean => {
		return chip === this._defaultColor;
	};

	/** return true if given chip is selected */
	isSelected = (chip: ColorSpec): boolean => {
		return chip.id === this._value?.id;
	};

	/** go to next page */
	@action nextPage = (): void => {
		this._page = Math.min(this.numberOfPages - 1, this._page + 1);
	};

	/** go to previous page */
	@action prevPage = (): void => {
		this._page = Math.max(0, this._page - 1);
	};

	/** update the color */
	@action setColors = (colors: ColorSpec[]): void => {
		const { _defaultColor } = this;

		if (_defaultColor) {
			this._colors = [_defaultColor, ...colors];
		} else {
			this._colors = colors;
		}
	};

	/** set selected chip */
	@action setValue = (chip?: ColorSpec): void => {
		if (this.colorIds.includes(chip?.id)) {
			this._value = chip;
			this._syncPage();
		}
	};

	/** set width */
	@action setWidth = (w: number): void => {
		const { _colors } = this;
		const n = _colors.length;

		if (WIDTH_PER_COLOR * n < w) {
			this._chipsPerPage = n;
		} else {
			this._chipsPerPage = Math.floor((w - OUTER_PADDING) / WIDTH_PER_COLOR);
		}

		this._width = w;
		this._syncPage();
	};

	/** calculate page when selected chip is located */
	_syncPage = (): void => {
		const { _chipsPerPage, _value, _width, colorIds } = this;

		if (_value && _width) {
			const index = colorIds.indexOf(_value.id);
			this._page = Math.floor(Math.max(index, 0) / _chipsPerPage);
		} else {
			this._page = 0;
		}
	};

	constructor(colors: ColorSpec[], defaultColor?: ColorSpec, value?: ColorSpec) {
		makeObservable(this);

		if (defaultColor) {
			this._defaultColor = defaultColor;
			this._colors = [defaultColor, ...colors];
		} else {
			this._colors = colors;
		}

		this._value = value || this._colors[0];
	}
}

export const ColorChipArrayStoreContext = createContext<ColorChipArrayStore>(undefined);
export const useColorChipArrayStore = (): ColorChipArrayStore => useContext(ColorChipArrayStoreContext);